import { tetriFromDecimal } from '@/utils/money';
import type {
    ShiftLedgerSummary,
    ShiftRegisterPage,
    ShiftRegisterRow,
} from '@/api/models';
import type {
    ShiftLedgerSummaryWire,
    ShiftRegisterItemWire,
    ShiftRegisterPageWire,
} from './responses';

/**
 * Wire ⇄ domain for shifts. The only file that knows the server's names.
 *
 * Two things happen here and nowhere else:
 *
 * 1. **Decimal GEL → integer tetri** for the four drawer figures. The shift
 *    entity stores them as decimals — it predates the app's tetri contract —
 *    and converting once at the edge is what keeps the rest of the screen on
 *    the contract. The conversion goes through `tetriFromDecimal`, which reads
 *    digit positions instead of multiplying: `50.07 * 100` is `5006.999…` in
 *    binary floating point, and rounding that is one more place for a
 *    hand-counted figure to lose a tetri (BOS-162).
 * 2. **`basis` is narrowed to the literal `'net'`.** The server sends a string
 *    so a future gross mode is a value change rather than a breaking one; a
 *    value this client does not know is treated as net rather than crashing the
 *    screen, because the numbers are still the numbers — but the type keeps
 *    every consumer honest about which policy it is rendering.
 */

const NET: ShiftLedgerSummary['basis'] = 'net';

/** Decimal GEL from the wire → integer tetri. `null` survives as `null`. */
const toTetri = (gel: number | null): number | null =>
    gel === null ? null : tetriFromDecimal(gel);

export const toShiftRegisterRow = (
    wire: ShiftRegisterItemWire,
): ShiftRegisterRow => ({
    id: wire.id,
    branchId: wire.branchId,
    branchName: wire.branchName,
    status: wire.status,
    openedAt: wire.openedAt,
    openedBySellerId: wire.openedBySellerId,
    openedByName: wire.openedByName,
    closedAt: wire.closedAt,
    closedBySellerId: wire.closedBySellerId,
    closedByName: wire.closedByName,
    // Non-null on the wire: a shift cannot open without a counted float.
    openingFloatTetri: toTetri(wire.openingFloat) ?? 0,
    closingCountTetri: toTetri(wire.closingCount),
    expectedAtCloseTetri: toTetri(wire.expectedAtClose),
    closeDiscrepancyTetri: toTetri(wire.closeDiscrepancy),
    closeJournalEntryId: wire.closeJournalEntryId ?? null,
    receiptCount: wire.receiptCount,
});

export const toShiftRegisterPage = (
    wire: ShiftRegisterPageWire,
): ShiftRegisterPage => ({
    rows: (wire.items ?? []).map(toShiftRegisterRow),
    page: wire.page,
    pageSize: wire.pageSize,
    totalCount: wire.totalCount,
    totalPages: wire.totalPages,
});

export const toShiftLedgerSummary = (
    wire: ShiftLedgerSummaryWire,
): ShiftLedgerSummary => ({
    shift: toShiftRegisterRow(wire.shift),
    basis: NET,
    accounts: wire.accounts ?? [],
    totalDebitTetri: wire.totalDebitTetri,
    totalCreditTetri: wire.totalCreditTetri,
    receiptsIncluded: wire.receiptsIncluded,
    receiptsReversed: wire.receiptsReversed,
    receiptsUnposted: wire.receiptsUnposted,
});
